import { Component, type ReactNode, type ErrorInfo } from 'react';
import { analytics } from '@/lib/analytics';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary that catches React runtime errors (hooks
 * violations, render crashes, etc.) and shows a branded error page
 * matching the Nitro server error aesthetic.
 *
 * Pure inline styles; zero dependencies on MUI, Tailwind, or Emotion
 * so it works even when the theme provider itself is the thing that broke.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const route = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    analytics.errorBoundaryHit(error.message, info.componentStack ?? undefined, route);
    analytics.captureException(error, { route, component_stack: info.componentStack?.slice(0, 1000) });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev =
      typeof import.meta !== 'undefined' &&
      (import.meta as any).env?.DEV === true;
    const message = this.state.error?.message ?? '';
    const stack = this.state.error?.stack ?? '';

    return <ErrorFallback isDev={isDev} message={message} stack={stack} onRetry={this.handleRetry} />;
  }
}

function ErrorFallback({
  isDev,
  message,
  stack,
  onRetry,
}: {
  isDev: boolean;
  message: string;
  stack: string;
  onRetry: () => void;
}) {
  return (
    <div style={styles.root}>
      {/* Animated grid */}
      <div style={styles.grid} />

      {/* Particles (CSS-only subset) */}
      <div style={styles.particles}>
        {PARTICLE_SEEDS.map((s, i) => (
          <div key={i} style={particleStyle(s)} />
        ))}
      </div>

      <div style={styles.scene}>
        {/* Status code */}
        <div style={styles.codeWrap}>
          <div style={styles.code}>500</div>
        </div>

        <div style={styles.icon}>💥</div>
        <h1 style={styles.title}>Client Error</h1>
        <p style={styles.flavor}>
          Something broke while rendering this page.
        </p>

        <div style={styles.actions}>
          <a href="/" style={{ ...styles.btn, ...styles.btnPrimary }}>
            <HomeIcon />
            Back to Home
          </a>
          <button
            onClick={onRetry}
            style={{ ...styles.btn, ...styles.btnSecondary }}
          >
            Try Again
          </button>
          <button
            onClick={() => location.reload()}
            style={{ ...styles.btn, ...styles.btnSecondary }}
          >
            Hard Reload
          </button>
        </div>

        {isDev && (message || stack) && (
          <details style={styles.devDetails} open>
            <summary style={styles.devSummary}>$ error --verbose</summary>
            {message && <pre style={styles.devPre}>{message}</pre>}
            {stack && (
              <pre style={{ ...styles.devPre, color: 'rgba(255,255,255,0.35)', marginTop: 8 }}>
                {stack}
              </pre>
            )}
          </details>
        )}

        <p style={styles.footer}>
          cdbattaglia.com &middot;{' '}
          <a href="https://github.com/cdbattags" target="_blank" rel="noreferrer" style={styles.footerLink}>
            github
          </a>
        </p>
      </div>

      <style>{KEYFRAMES}</style>
    </div>
  );
}

function HomeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

const KEYFRAMES = `
@keyframes eb-gridDrift { to { background-position: 40px 40px; } }
@keyframes eb-codeGlow {
  0%   { filter: brightness(1)    drop-shadow(0 0 20px rgba(239,68,68,0.3)); }
  100% { filter: brightness(1.15) drop-shadow(0 0 40px rgba(168,85,247,0.4)); }
}
@keyframes eb-iconPulse {
  0%, 100% { transform: scale(1);   opacity: 0.8; }
  50%      { transform: scale(1.1); opacity: 1;   }
}
@keyframes eb-particleDrift {
  0%   { opacity: 0;   transform: translateY(100vh) scale(0); }
  10%  { opacity: 0.6; }
  90%  { opacity: 0.6; }
  100% { opacity: 0;   transform: translateY(-10vh)  scale(1); }
}
`;

const PARTICLE_COLORS = ['#ef4444', '#a855f7', '#06b6d4', '#60a5fa', '#f472b6'];

const PARTICLE_SEEDS = Array.from({ length: 15 }, (_, i) => ({
  left: ((i * 37 + 13) % 100),
  color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
  duration: 8 + (i * 1.3) % 12,
  delay: (i * 0.7) % 10,
  size: 2 + (i % 3),
}));

function particleStyle(s: typeof PARTICLE_SEEDS[number]): React.CSSProperties {
  return {
    position: 'absolute',
    left: `${s.left}%`,
    width: s.size,
    height: s.size,
    borderRadius: '50%',
    background: s.color,
    opacity: 0,
    animation: `eb-particleDrift ${s.duration}s linear ${s.delay}s infinite`,
  };
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#0f0f13',
    color: '#e0e0e0',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  grid: {
    position: 'fixed',
    inset: '0',
    backgroundImage:
      'linear-gradient(rgba(239,68,68,0.04) 1px,transparent 1px),' +
      'linear-gradient(90deg,rgba(239,68,68,0.04) 1px,transparent 1px)',
    backgroundSize: '40px 40px',
    animation: 'eb-gridDrift 20s linear infinite',
  },
  particles: {
    position: 'fixed',
    inset: '0',
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  scene: {
    position: 'relative',
    textAlign: 'center',
    maxWidth: 560,
    padding: '2rem',
    zIndex: 1,
  },
  codeWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  code: {
    fontSize: '7rem',
    fontWeight: 900,
    letterSpacing: '-0.04em',
    lineHeight: 1,
    color: 'transparent',
    background: 'linear-gradient(135deg, #ef4444, #a855f7, #06b6d4)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    animation: 'eb-codeGlow 4s ease-in-out infinite alternate',
    userSelect: 'none',
  },
  icon: {
    fontSize: '2rem',
    marginBottom: '0.75rem',
    animation: 'eb-iconPulse 2s ease-in-out infinite',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '0.5rem',
  },
  flavor: {
    fontSize: '0.95rem',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '2rem',
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '2rem',
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.6rem 1.4rem',
    borderRadius: 8,
    fontSize: '0.9rem',
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    background: '#06b6d4',
    color: '#fff',
  },
  btnSecondary: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(255,255,255,0.15)',
  },
  devDetails: {
    textAlign: 'left' as const,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '1.25rem',
    marginBottom: '1.5rem',
    maxHeight: 300,
    overflowY: 'auto' as const,
  },
  devSummary: {
    cursor: 'pointer',
    fontFamily: '"Fira Code","Cascadia Code",Consolas,monospace',
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.4)',
    userSelect: 'none' as const,
  },
  devPre: {
    fontFamily: '"Fira Code","Cascadia Code",Consolas,monospace',
    fontSize: '0.75rem',
    color: '#ef4444',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    marginTop: '0.75rem',
    lineHeight: 1.6,
  },
  footer: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.2)',
  },
  footerLink: {
    color: 'rgba(255,255,255,0.3)',
    textDecoration: 'none',
  },
};
